import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../hooks/useI18n';
import { entityConfigApi } from '../services/entityConfigApi';
import { EntityConfig } from '../types';
import { hasPermission, isAdmin, isSubscriber, isAgent, PERMISSIONS } from '../utils/authUtils';
import {
  X,
  Leaf,
  ChevronDown,
  ChevronUp,
  Home,
  Users,
  BarChart3,
  FileText,
  FileSpreadsheet,
  Shield,
  Settings,
  LogOut,
  User,
  Activity,
  TrendingUp,
  Database,
  Bell,
  Building2,
  UserCheck,
  DollarSign,
  Palette,
  Package,
  Layers,
  ShoppingCart,
  History,
  Truck
} from 'lucide-react';

import ExportModal from './ExportModal';
import EDIExportModal from './EDIExportModal';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showEdiModal, setShowEdiModal] = useState(false);
  const { user, logout } = useAuth();
  const { t, isRTL } = useI18n();
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [entityConfig, setEntityConfig] = useState<EntityConfig | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      if (user?.entityId) {
        try {
          const config = await entityConfigApi.getConfigByEntityId(user.entityId);
          setEntityConfig(config);
        } catch (error) {
          console.error('Failed to fetch entity config:', error);
        }
      }
    };
    fetchConfig();
  }, [user?.entityId]);

  // Build navigation items based on user permissions
  const getNavItems = () => {
    const items = [];

    // Dashboard - available to all authenticated users
    if (hasPermission(PERMISSIONS.DASHBOARD_ACCESS)) {
      if (isAdmin()) {
        items.push({ path: '/admin/dashboard', label: t('sidebar.dashboard'), icon: Home });
      } else {
        items.push({ path: '/dashboard', label: t('sidebar.dashboard'), icon: Home });
      }
    }

    // Profile - available to all authenticated users
    if (hasPermission(PERMISSIONS.PROFILE_READ)) {
      items.push({ path: '/profile', label: t('sidebar.profile'), icon: User });
    }

    // Companies - available to users with company read permission
    if (hasPermission(PERMISSIONS.COMPANY_READ)) {
      items.push({ path: '/companies', label: t('sidebar.companies'), icon: Building2 });
    }

    // Clients - available to users with client read permission
    if (hasPermission(PERMISSIONS.CLIENT_READ)) {
      items.push({ path: '/clients', label: t('sidebar.clients'), icon: UserCheck });
    }

    // Suppliers - available to users with client read permission (assuming same for now)
    if (hasPermission(PERMISSIONS.CLIENT_READ)) {
      items.push({ path: '/suppliers', label: t('suppliers.title'), icon: Truck });
    }

    // Invoices - available to users with invoice read permission
    if (hasPermission(PERMISSIONS.INVOICE_READ)) {
      items.push({ path: '/invoices', label: t('sidebar.invoices'), icon: DollarSign });
    }

    // Templates - available to users with template access
    if (entityConfig?.customTemplatesEnabled) {
      items.push({ path: '/templates', label: t('sidebar.templates'), icon: Palette });
    }

    // Stock Management - available to users with stock read permission (assuming same as invoice for now)
    // AND STOCK_FEATURE permission (if enabled for the entity)
    if (hasPermission(PERMISSIONS.INVOICE_READ) && hasPermission(PERMISSIONS.STOCK_FEATURE)) {
      items.push({ path: '/categories', label: t('stock.categories.title'), icon: Layers });
      items.push({ path: '/products', label: t('stock.products.title'), icon: Package });
      items.push({ path: '/orders', label: t('stock.orders.title'), icon: ShoppingCart });
      items.push({ path: '/stock-movements', label: t('stock.movements.title'), icon: History });
    }

    // Agents - only available to subscribers with agent management permissions
    if (hasPermission(PERMISSIONS.AGENT_READ)) {
      items.push({ path: '/agents', label: t('sidebar.agents'), icon: Users });
    }

    // Admin-specific items
    if (isAdmin()) {
      items.push({ path: '/admin/entity-configs', label: 'Entity Configs', icon: Settings });
      items.push({ path: '/admin/app-config', label: 'App Config', icon: Settings });
    }

    return items;
  };

  const navItems = getNavItems();

  const quickActions = [
    //  { label: t('sidebar.viewAnalytics'), icon: TrendingUp, action: () => { } },
    //  { label: t('sidebar.generateReport'), icon: FileText, action: () => { } },
    //  { label: t('sidebar.systemStatus'), icon: Activity, action: () => { } },
    { label: t('sidebar.dataExport'), icon: Database, action: () => setShowExportModal(true) },
    { label: t('sidebar.ediExport'), icon: FileSpreadsheet, action: () => setShowEdiModal(true) }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleLogout = () => {
    logout();
    onClose();
  };

  const getInitials = (email: string) => {
    return email.split('@')[0].substring(0, 2).toUpperCase();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="sidebar-overlay lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`sidebar flex flex-col ${isRTL ? 'rtl' : 'ltr'} lg:translate-x-0 ${isOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full' : '-translate-x-full')
          }`}
        style={{
          [isRTL ? 'right' : 'left']: '0',
          [isRTL ? 'left' : 'right']: 'auto',
          [isRTL ? 'borderLeft' : 'borderRight']: '1px solid #e5e7eb',
          [isRTL ? 'borderRight' : 'borderLeft']: 'none'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <Link to="/" className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
              <img src="/solvingers.svg" />
            </div>
            <h1 className="text-xl font-bold text-secondary-900">Facturelik</h1>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg hover:bg-secondary-100 transition-colors"
          >
            <X className="w-5 h-5 text-secondary-600" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">
              {t('sidebar.navigation')}
            </h3>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  onClick={onClose}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="mt-8">
            <div
              className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary-50 cursor-pointer transition-colors"
              onClick={() => setShowQuickActions(!showQuickActions)}
            >
              <h3 className="text-sm font-medium text-secondary-700">{t('sidebar.quickActions')}</h3>
              {showQuickActions ? (
                <ChevronUp className="w-4 h-4 text-secondary-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-secondary-400" />
              )}
            </div>

            {showQuickActions && (
              <div className="mt-2 space-y-1">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        action.action();
                        // onClose(); // Don't close sidebar if opening modal
                      }}
                      className={`w-full flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'} px-4 py-2 text-sm text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{action.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-secondary-200 bg-secondary-50">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} mb-2`}>
            <div className="w-2 h-2 bg-success-500 rounded-full"></div>
            <span className='text-xs text-secondary-600'>{t('sidebar.systemOnline')}</span>
          </div>
          <p className="text-xs text-secondary-500">Facturelik v5-Beta</p>
        </div>
      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      <EDIExportModal
        isOpen={showEdiModal}
        onClose={() => setShowEdiModal(false)}
      />
    </>
  );
};

export default Sidebar;
